// apps/web/src/features/dashboards/china-requests/api.ts (#1589)
// Thin wrapper over `request<T>` for the leadership China-requests
// dashboard (#1588 endpoint). The Zod schema is forwarded so the
// response is validated at the boundary — drift between `@magimp/shared`
// and the backend surface fails loudly here instead of corrupting React
// state silently. Mirrors `finance/revenue/api.ts`.
import type { ZodType } from 'zod';
import {
  ChinaRequestsDashboardSummarySchema,
  ChinaRequestsDashboardTrendSchema,
  type ChinaRequestsDashboardSummary,
  type ChinaRequestsDashboardTrend,
  type ChinaRequestsSummaryQuery,
} from '@magimp/shared';
import { request } from '../../../api/client.js';

// Non-custom presets send only `period`; the server derives the window. Shared
// by both dashboard fetchers so the query-param shape stays identical.
function periodParams(query: ChinaRequestsSummaryQuery): Record<string, string> {
  const params: Record<string, string> = { period: query.period };
  if (query.period === 'custom' && query.from && query.to) {
    params.from = query.from;
    params.to = query.to;
  }
  return params;
}

export function fetchChinaRequestsDashboard(
  query: ChinaRequestsSummaryQuery,
  opts: { signal?: AbortSignal } = {},
): Promise<ChinaRequestsDashboardSummary> {
  const requestOpts: { signal?: AbortSignal; query: Record<string, string> } = {
    query: periodParams(query),
  };
  if (opts.signal) requestOpts.signal = opts.signal;
  return request<ChinaRequestsDashboardSummary>(
    '/dashboards/china-requests/summary',
    { method: 'GET' },
    requestOpts,
    ChinaRequestsDashboardSummarySchema as unknown as ZodType<ChinaRequestsDashboardSummary>,
  );
}

// Trend series for the SAME period window — a SEPARATE request from the summary
// (the chart loads lazily so the headline numbers render first).
export function fetchChinaRequestsTrend(
  query: ChinaRequestsSummaryQuery,
  opts: { signal?: AbortSignal } = {},
): Promise<ChinaRequestsDashboardTrend> {
  const requestOpts: { signal?: AbortSignal; query: Record<string, string> } = {
    query: periodParams(query),
  };
  if (opts.signal) requestOpts.signal = opts.signal;
  return request<ChinaRequestsDashboardTrend>(
    '/dashboards/china-requests/trend',
    { method: 'GET' },
    requestOpts,
    ChinaRequestsDashboardTrendSchema as unknown as ZodType<ChinaRequestsDashboardTrend>,
  );
}
